import type { Fund } from "@/data/funds";
import { FUND_CATEGORIES, type FundCategoryKey } from "@/lib/fund-categories";
import { cn } from "@/lib/utils";

interface FundFiltersProps {
  activeCategory: FundCategoryKey;
  onCategoryChange: (key: FundCategoryKey) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  funds: readonly Fund[];
  countsOverride?: Partial<Record<FundCategoryKey, number>>;
}

function getCategoryCount(funds: readonly Fund[], key: FundCategoryKey): number {
  if (key === "all") return funds.length;
  // DDB uses `fundType` — the local `type` alias is only on FundDetail.
  return funds.filter((f) => f.fundType === key).length;
}

export function FundFilters({
  activeCategory,
  onCategoryChange,
  searchQuery,
  onSearchChange,
  funds,
  countsOverride,
}: FundFiltersProps) {
  return (
    <div className="flex items-center justify-between border-b border-[var(--etf-border)] bg-[var(--etf-black2)] px-10 max-md:px-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div className="flex flex-nowrap">
        {FUND_CATEGORIES.map((cat) => {
          const isActive = activeCategory === cat.key;
          const count = countsOverride?.[cat.key] ?? getCategoryCount(funds, cat.key);
          const isHidden = cat.key !== "all" && funds.length > 0 && count === 0;
          if (isHidden) return null;
          return (
            <button
              type="button"
              key={cat.key}
              onClick={() => onCategoryChange(cat.key)}
              className={cn(
                "flex-shrink-0 flex items-center gap-2 whitespace-nowrap border-b-2 bg-transparent px-6 py-[18px] text-sm transition-all duration-150 first:pl-0",
                "tracking-[0.06em] cursor-pointer",
                isActive
                  ? "border-[var(--etf-orange)] text-[var(--etf-white)]"
                  : "border-transparent text-[var(--etf-white3)] hover:text-[var(--etf-white2)]",
              )}
            >
              {cat.label}
              <span
                className={cn(
                  "rounded-[10px] px-1.5 py-0.5 text-[9px]",
                  isActive
                    ? "bg-[rgba(255,85,0,0.15)] text-[var(--etf-orange)]"
                    : "bg-white/[0.08] text-[var(--etf-white3)]",
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 border border-[var(--etf-border)] bg-[var(--etf-black3)] px-3.5 py-1.5">
        <svg
          className="size-3 text-[var(--etf-white3)]"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 256 256"
          fill="currentColor"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z" />
        </svg>
        <input
          type="text"
          placeholder="Search funds..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-[180px] border-none bg-transparent font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white)] outline-none placeholder:text-[var(--etf-white3)]"
        />
      </div>
    </div>
  );
}
